import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ConvertToAdmin = ({ onClose, onSuccess }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [converting, setConverting] = useState(false);
  const searchTimeoutRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch();
      }, 300);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const performSearch = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/users/search-for-admin`, {
        params: {
          search: searchQuery,
          limit: 50,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      setSearchResults(response.data.data || []);
    } catch (err) {
      console.error('Failed to search users:', err);
      toast.error('Failed to search users');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.find(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleConvert = async () => {
    if (selectedUsers.length === 0) {
      toast.warning('Please select at least one user to convert');
      return;
    }

    try {
      setConverting(true);
      const userIds = selectedUsers.map(user => user.id);

      const response = await axios.post(
        `${API_BASE_URL}/admin/users/convert-to-admin`,
        { userIds },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      toast.success(response.data.message || `Successfully converted ${selectedUsers.length} user(s) to admin`);
      
      // Reset state
      setSelectedUsers([]);
      setSearchQuery('');
      setSearchResults([]);
      
      // Notify parent to refresh
      if (onSuccess) {
        onSuccess();
      }
      
      // Close after a short delay
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (err) {
      console.error('Failed to convert users:', err);
      const errorMessage = err.response?.data?.error || 'Failed to convert users to admin';
      toast.error(errorMessage);
    } finally {
      setConverting(false);
    }
  };

  const isUserSelected = (userId) => {
    return selectedUsers.some(user => user.id === userId);
  };

  return (
    <div style={{ padding: '20px' }}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      
      <div className="mb-4">
        <h4>Convert Users to Admin</h4>
        <p className="text-muted">Search for users by name or email, select them, and convert to admin (overrides current userType)</p>
      </div>

      <div className="mb-3">
        <label className="form-label">Search Users (by name or email)</label>
        <input
          type="text"
          className="form-control"
          placeholder="Type at least 2 characters to search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={converting}
        />
      </div>

      {loading && (
        <div className="text-center my-3">
          <p>Searching...</p>
        </div>
      )}

      {selectedUsers.length > 0 && (
        <div className="mb-3 p-3 bg-light rounded">
          <strong>Selected Users ({selectedUsers.length}):</strong>
          <div className="mt-2">
            {selectedUsers.map(user => (
              <span key={user.id} className="badge bg-primary me-2 mb-2">
                {user.fullName || user.email}
                <button
                  className="ms-2 border-0 bg-transparent text-white"
                  onClick={() => toggleUserSelection(user)}
                  style={{ cursor: 'pointer' }}
                  disabled={converting}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="mb-3">
          <label className="form-label">Search Results:</label>
          <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
            <table className="table table-hover mb-0">
              <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={searchResults.length > 0 && searchResults.every(user => isUserSelected(user.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const newUsers = searchResults.filter(user => !isUserSelected(user.id));
                          setSelectedUsers(prev => [...prev, ...newUsers]);
                        } else {
                          const userIds = searchResults.map(user => user.id);
                          setSelectedUsers(prev => prev.filter(user => !userIds.includes(user.id)));
                        }
                      }}
                      disabled={converting}
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Current User Type</th>
                  <th>Verified</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map(user => (
                  <tr key={user.id} style={{ cursor: 'pointer' }} onClick={() => toggleUserSelection(user)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isUserSelected(user.id)}
                        onChange={() => toggleUserSelection(user)}
                        disabled={converting}
                      />
                    </td>
                    <td>{user.fullName || 'N/A'}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className="badge bg-secondary">
                        {user.userType || 'user'}
                      </span>
                    </td>
                    <td>
                      {user.isVerified ? (
                        <span className="badge bg-success">Yes</span>
                      ) : (
                        <span className="badge bg-warning">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {searchQuery.length > 0 && searchQuery.length < 2 && (
        <div className="alert alert-info">
          Please type at least 2 characters to search
        </div>
      )}

      {searchQuery.length >= 2 && !loading && searchResults.length === 0 && (
        <div className="alert alert-warning">
          No users found matching "{searchQuery}"
        </div>
      )}

      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button color="secondary" onClick={onClose} disabled={converting}>
          Cancel
        </Button>
        <Button 
          color="primary" 
          onClick={handleConvert}
          disabled={selectedUsers.length === 0 || converting}
        >
          {converting ? 'Converting...' : `Convert ${selectedUsers.length} User(s) to Admin`}
        </Button>
      </div>
    </div>
  );
};

export default ConvertToAdmin;
